/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: OutboundRoutesPage.tsx
 🗓️ Created: 2025-01-14
 🧩 Description:
    Main page component for Outbound Routes (ARS) management.
    Manages call routing to external gateways based on dial patterns.

 🔧 Notes:
    - Lists all Outbound Routes for the current domain
    - Supports create, edit, duplicate, and delete operations
    - Shows pattern and gateway counts
    - Uses domain-scoped data

 🔁 Last Updated: 2025-01-14 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Edit, Trash2, Copy, Route } from 'lucide-react';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import {
  deleteOutboundRouteById,
  navigateToOutboundRouteDuplicate,
} from './outboundRoutesActions';
import { useOutboundRouteList } from './hooks/useOutboundRouteList';
import { OutboundRoute } from './types';


const ACTION_BUTTON_CLASSNAMES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
};

const renderStrategy = (
  routingStrategy: string,
  translate: ReturnType<typeof useI18n>['t'],
) => (
  <span className="text-sm text-muted-foreground">
    {translate(`outboundRoutes.strategies.${routingStrategy}`)}
  </span>
);

const renderCount = (value: number | undefined) => (
  <span className="text-sm font-medium text-foreground">{value ?? 0}</span>
);

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

export function OutboundRoutesPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const pageTitle = useMemo(() => t('outboundRoutes.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState<OutboundRoute | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);


  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useOutboundRouteList();

  // Handlers
  const handleCreate = () => {
    navigate('/pbx/calls-routing/outbound/new');
  };

  const handleEdit = (route: OutboundRoute) => {
    navigate(`/pbx/calls-routing/outbound/${route.uuid ?? route.id}`);
  };

  const handleDuplicate = (route: OutboundRoute) => {
    navigateToOutboundRouteDuplicate(navigate, route.uuid ?? route.id);
  };

  const handleDelete = useCallback((route: OutboundRoute) => {
    setRouteToDelete(route);
    setShowDeleteModal(true);
  }, []);

  // Confirm delete
  const confirmDelete = useCallback(async () => {
    if (!routeToDelete) return;

    setIsDeleting(true);
    try {
      await deleteOutboundRouteById(routeToDelete.uuid ?? routeToDelete.id);

      showSuccess(t('outboundRoutes.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setRouteToDelete(null);
      refresh();
    } catch {
      showError(t('outboundRoutes.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [routeToDelete, showError, showSuccess, t, refresh]);

  // Render actions
  const renderActions = useCallback(
    (route: OutboundRoute) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common:edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(route)}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.edit}`}
            aria-label={t('common:edit')}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common:duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(route)}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.duplicate}`}
            aria-label={t('common:duplicate')}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common:delete')} position="top">
          <button
            type="button"
            onClick={() => handleDelete(route)}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.delete}`}
            aria-label={t('common:delete')}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleEdit, handleDuplicate, handleDelete, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            titleIcon={<Route className="h-5 w-5 text-primary" />}
            columns={columns.map((column) => ({
              ...column,
              render:
                column.key === 'name'
                  ? (_value: unknown, item: OutboundRoute) => (
                      <span className="font-medium text-foreground">
                        {item.name}
                      </span>
                    )
                  : column.key === 'routingStrategy'
                    ? (_value: unknown, item: OutboundRoute) =>
                        renderStrategy(item.routingStrategy, t)
                    : column.key === 'patterns'
                      ? (_value: unknown, item: OutboundRoute) =>
                          renderCount(item._count?.patterns)
                      : column.key === 'gateways'
                        ? (_value: unknown, item: OutboundRoute) =>
                            renderCount(item._count?.gateways)
                        : column.key === 'createdAt'
                          ? (_value: unknown, item: OutboundRoute) => (
                              <span className="text-sm text-muted-foreground">
                                {formatDate(item.createdAt)}
                              </span>
                            )
                          : undefined,
            }))}
            onColumnToggle={handleColumnToggle}
            loading={loading}
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            searchQuery={searchQuery}
            onSearch={handleSearch}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onAdd={handleCreate}
          onDuplicate={handleDuplicate}
            title={t('outboundRoutes.title')}
            addButtonText={t('outboundRoutes.actions.add')}
            searchPlaceholder={t('common.search')}
            renderActions={renderActions}
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title={t('outboundRoutes.deleteRoute')}
            message={t('outboundRoutes.messages.confirmDelete') + ' ' + t('outboundRoutes.messages.deleteWarning')}
            confirmText={t('outboundRoutes.actions.delete')}
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}

export default OutboundRoutesPage;

